<template>
  <div id="layout-wrapper" :class="{ 'nav-collapsed': isNavCollapsed }">
    <NavigationBar @nav-toggle="handleNavToggle" />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Organisation Structure</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item">
                      <a href="javascript:void(0)" @click="gotTo('dashboard')">Dashboard</a>
                    </li>
                    <li class="breadcrumb-item active">System Management / Organization Structure</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-4">
              <div class="card h-100">
                <div class="card-header bg-light d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">
                    <i class="mdi mdi-family-tree text-primary me-2"></i>
                    Units
                  </h5>
                  <button class="btn btn-sm btn-outline-primary" @click="openNewUnit">
                    <i class="mdi mdi-plus me-1"></i>
                    New
                  </button>
                </div>
                <div class="card-body p-0">
                  <div v-if="loadingUnits" class="text-center py-4">
                    <div class="spinner-border text-primary" role="status"></div>
                    <p class="mb-0 mt-2">Loading organisation structure...</p>
                  </div>
                  <div v-else class="list-group list-group-flush org-unit-list">
                    <button
                      v-for="unit in flatUnits"
                      :key="unit.id"
                      class="list-group-item list-group-item-action text-start"
                      :class="{ active: selectedUnitId === unit.id }"
                      :style="{ paddingLeft: 12 + (unit.level * 20) + 'px' }"
                      @click="selectUnit(unit.id)"
                    >
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{{ unit.name }}</strong>
                          <div class="text-muted small">
                            {{ unit.code || '—' }}
                          </div>
                        </div>
                        <span class="badge bg-light text-dark">{{ unit.membersCount }}</span>
                      </div>
                      <div class="mt-1">
                        <small class="text-muted">
                          Manager: {{ unit.managerName || 'Not set' }}
                        </small>
                      </div>
                    </button>
                    <div v-if="flatUnits.length === 0" class="text-center text-muted py-4">
                      <i class="mdi mdi-information-outline font-size-36 d-block mb-2"></i>
                      <p class="mb-0">No units yet. Create one to get started.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-8">
              <div class="card mb-4">
                <div class="card-header bg-light d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">
                    <i class="mdi mdi-home-modern text-primary me-2"></i>
                    {{ unitForm.id ? 'Edit Unit' : 'Create Unit' }}
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Name <span class="text-danger">*</span></label>
                      <input type="text" v-model="unitForm.name" class="form-control" placeholder="e.g. Finance Department">
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Code</label>
                      <input type="text" v-model="unitForm.code" class="form-control" placeholder="e.g. FIN">
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Parent Unit</label>
                      <select v-model="unitForm.parentId" class="form-select">
                        <option value="">— None (Top Level) —</option>
                        <option v-for="unit in flatUnits" :key="'parent-' + unit.id" :value="unit.id">
                          {{ '—'.repeat(unit.level) }} {{ unit.name }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Manager (EmpNo / ID / Email)</label>
                      <input type="text" v-model="unitForm.managerIdentifier" class="form-control" placeholder="Enter identifier">
                    </div>
                    <div class="col-12">
                      <label class="form-label">Description</label>
                      <textarea v-model="unitForm.description" rows="2" class="form-control" placeholder="Optional description"></textarea>
                    </div>
                  </div>
                </div>
                <div class="card-footer d-flex justify-content-between">
                  <button class="btn btn-outline-secondary" @click="resetUnitForm">
                    <i class="mdi mdi-refresh me-1"></i>
                    Reset
                  </button>
                  <div class="d-flex gap-2">
                    <button class="btn btn-danger" v-if="unitForm.id" @click="deleteUnit" :disabled="savingUnit || deletingUnit">
                      <span v-if="deletingUnit" class="spinner-border spinner-border-sm me-1"></span>
                      Delete
                    </button>
                    <button class="btn btn-primary" @click="saveUnit" :disabled="savingUnit">
                      <span v-if="savingUnit" class="spinner-border spinner-border-sm me-1"></span>
                      Save Unit
                    </button>
                  </div>
                </div>
              </div>

              <div class="card">
                <div class="card-header bg-light d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">
                    <i class="mdi mdi-account-group text-primary me-2"></i>
                    Unit Members
                  </h5>
                  <button class="btn btn-sm btn-outline-primary" @click="toggleMemberForm = !toggleMemberForm">
                    <i class="mdi" :class="toggleMemberForm ? 'mdi-minus' : 'mdi-plus'"></i>
                    {{ toggleMemberForm ? 'Hide Form' : 'Add Member' }}
                  </button>
                </div>
                <div class="card-body">
                  <div v-if="!selectedUnitId" class="text-muted">
                    Select a unit to manage its members.
                  </div>
                  <div v-else>
                    <div v-if="toggleMemberForm" class="border rounded p-3 mb-3">
                      <div class="row g-3">
                        <div class="col-md-4">
                          <label class="form-label">Employee Identifier <span class="text-danger">*</span></label>
                          <select v-model="memberForm.identifier" class="form-select" :disabled="loadingEmployees">
                            <option value="">Select employee</option>
                            <option
                              v-for="employee in employeeOptions"
                              :key="'employee-' + employee.id"
                              :value="employee.empNo || employee.nationalId"
                            >
                              {{ employeeOptionLabel(employee) }}
                            </option>
                          </select>
                        </div>
                        <div class="col-md-4">
                          <label class="form-label">Role</label>
                          <input type="text" v-model="memberForm.role" class="form-control" placeholder="e.g. Associate">
                        </div>
                        <div class="col-md-4 d-flex align-items-center">
                          <div class="form-check me-3">
                            <input class="form-check-input" type="checkbox" v-model="memberForm.isPrimary" id="primaryMember">
                            <label class="form-check-label" for="primaryMember">
                              Primary Assignment
                            </label>
                          </div>
                          <div class="form-check">
                            <input class="form-check-input" type="checkbox" v-model="memberForm.setAsManager" id="managerMember">
                            <label class="form-check-label" for="managerMember">
                              Set as Manager
                            </label>
                          </div>
                        </div>
                        <div class="col-12">
                          <label class="form-label">Supervisor</label>
                          <select v-model="memberForm.reportsTo" class="form-select" :disabled="loadingEmployees || memberForm.setAsManager">
                            <option value="">Use unit manager</option>
                            <option
                              v-for="supervisor in supervisorOptions"
                              :key="'supervisor-' + supervisor.id"
                              :value="supervisor.empNo || supervisor.nationalId"
                            >
                              {{ employeeOptionLabel(supervisor) }}
                            </option>
                          </select>
                          <small class="text-muted">
                            This reporting relationship controls which supervisor can approve the employee's leave.
                          </small>
                        </div>
                      </div>
                      <div class="text-end mt-3">
                        <button class="btn btn-success" @click="assignMember" :disabled="addingMember">
                          <span v-if="addingMember" class="spinner-border spinner-border-sm me-1"></span>
                          Assign Member
                        </button>
                      </div>
                    </div>

                    <div v-if="currentMembers.length === 0" class="text-center text-muted py-4">
                      <i class="mdi mdi-information-outline font-size-36 d-block mb-2"></i>
                      <p class="mb-0">No members assigned to this unit yet.</p>
                    </div>
                    <div v-else class="table-responsive">
                      <table class="table table-hover">
                        <thead class="table-light">
                        <tr>
                          <th>Employee</th>
                          <th>Role</th>
                          <th>Supervisor</th>
                          <th>Primary</th>
                          <th class="text-end">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="member in currentMembers" :key="member.id">
                          <td>
                            <strong>{{ member.employeeName || member.empNo }}</strong>
                            <div class="text-muted small">{{ member.positionTitle || '—' }}</div>
                          </td>
                          <td>{{ member.role || '—' }}</td>
                          <td>{{ member.supervisorName || member.supervisorEmpNo || 'Unit manager / unassigned' }}</td>
                          <td>
                            <span class="badge" :class="member.is_primary ? 'bg-success' : 'bg-light text-dark'">
                              {{ member.is_primary ? 'Yes' : 'No' }}
                            </span>
                          </td>
                          <td class="text-end">
                            <button class="btn btn-outline-danger btn-sm" @click="removeMember(member)" :disabled="removingMember">
                              <span v-if="removingMember" class="spinner-border spinner-border-sm me-1"></span>
                              Remove
                            </button>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";

export default {
  name: "OrgStructureManagement",
  components: { NavigationBar },
  data() {
    return {
      isNavCollapsed: false,
      loadingUnits: false,
      loadingEmployees: false,
      savingUnit: false,
      deletingUnit: false,
      addingMember: false,
      removingMember: false,
      toggleMemberForm: false,
      flatUnits: [],
      treeUnits: [],
      selectedUnitId: null,
      currentMembers: [],
      employeeOptions: [],
      unitForm: {
        id: null,
        name: "",
        code: "",
        parentId: "",
        managerIdentifier: "",
        description: ""
      },
      memberForm: {
        identifier: "",
        role: "",
        isPrimary: false,
        setAsManager: false,
        reportsTo: ""
      }
    };
  },
  mounted() {
    this.fetchSupervisorOptions();
    this.fetchOrgUnits();
  },
  computed: {
    supervisorOptions() {
      const selected = String(this.memberForm.identifier || "").trim().toUpperCase();
      return this.employeeOptions.filter((employee) => {
        const empNo = String(employee.empNo || "").trim().toUpperCase();
        const nationalId = String(employee.nationalId || "").trim().toUpperCase();
        return !selected || (empNo !== selected && nationalId !== selected);
      });
    }
  },
  methods: {
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },
    employeeOptionLabel(employee) {
      const name = String(employee.employeeName || "").trim() ||
        [employee.firstName, employee.middleName, employee.lastName].filter(Boolean).join(" ").trim();
      const id = employee.empNo || employee.nationalId || "";
      const department = employee.departmentName ? ` · ${employee.departmentName}` : "";
      return `${name || id} (${id})${department}`;
    },
    async fetchSupervisorOptions() {
      this.loadingEmployees = true;
      try {
        const data = new FormData();
        data.append("function", "getSupervisorAssignmentOptions");
        const res = await execute(data, authUrl);
        if (!res?.data?.success) {
          throw new Error(res?.data?.message || "Failed to load employees");
        }
        this.employeeOptions = Array.isArray(res.data.data?.employees) ? res.data.data.employees : [];
      } catch (error) {
        this.employeeOptions = [];
        this.$toast?.error(error.message || "Failed to load employees") ||
          console.error("Error loading supervisor options:", error);
      } finally {
        this.loadingEmployees = false;
      }
    },
    async fetchOrgUnits() {
      this.loadingUnits = true;
      try {
        const data = new FormData();
        data.append("function", "getOrgUnits");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.treeUnits = res.data.data.tree || [];
          this.flatUnits = res.data.data.flat || [];
          if (!this.selectedUnitId && this.flatUnits.length > 0) {
            this.selectUnit(this.flatUnits[0].id);
          } else if (this.selectedUnitId) {
            this.selectUnit(this.selectedUnitId);
          }
        } else {
          this.treeUnits = [];
          this.flatUnits = [];
          this.selectedUnitId = null;
          this.currentMembers = [];
          this.$toast?.error(res?.data?.message || "Failed to load organisation units") ||
            console.error(res?.data?.message || "Failed to load organisation units");
        }
      } catch (error) {
        this.treeUnits = [];
        this.flatUnits = [];
        this.selectedUnitId = null;
        this.currentMembers = [];
        this.$toast?.error("Error: " + error.message) ||
          console.error("Error loading organisation units:", error);
      } finally {
        this.loadingUnits = false;
      }
    },
    findUnitInTree(id, units = null) {
      const list = units || this.treeUnits;
      for (const unit of list) {
        if (unit.id === id) {
          return unit;
        }
        if (unit.children && unit.children.length) {
          const found = this.findUnitInTree(id, unit.children);
          if (found) return found;
        }
      }
      return null;
    },
    selectUnit(id) {
      this.selectedUnitId = id;
      const unit = this.findUnitInTree(id);
      if (!unit) {
        this.currentMembers = [];
        this.resetUnitForm();
        return;
      }
      this.unitForm = {
        id: unit.id,
        name: unit.name,
        code: unit.code || "",
        parentId: unit.parent_id || "",
        managerIdentifier: unit.manager_emp_no || "",
        description: unit.description || ""
      };
      this.currentMembers = unit.members || [];
    },
    openNewUnit() {
      this.resetUnitForm();
      this.selectedUnitId = null;
      this.currentMembers = [];
    },
    resetUnitForm() {
      this.unitForm = {
        id: null,
        name: "",
        code: "",
        parentId: "",
        managerIdentifier: "",
        description: ""
      };
    },
    async saveUnit() {
      if (!this.unitForm.name) {
        this.$toast?.error("Name is required") || alert("Name is required");
        return;
      }
      this.savingUnit = true;
      try {
        const data = new FormData();
        data.append("function", "saveOrgUnit");
        if (this.unitForm.id) data.append("unitId", this.unitForm.id);
        data.append("name", this.unitForm.name);
        data.append("code", this.unitForm.code || "");
        data.append("parentId", this.unitForm.parentId || "");
        data.append("description", this.unitForm.description || "");
        data.append("managerIdentifier", this.unitForm.managerIdentifier || "");

        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message || "Unit saved") || alert("Unit saved");
          await this.fetchOrgUnits();
        } else {
          throw new Error(res?.data?.message || "Failed to save unit");
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) || alert("Error: " + error.message);
      } finally {
        this.savingUnit = false;
      }
    },
    async deleteUnit() {
      if (!this.unitForm.id) return;
      if (!confirm("Are you sure you want to delete this unit?")) return;
      this.deletingUnit = true;
      try {
        const data = new FormData();
        data.append("function", "deleteOrgUnit");
        data.append("unitId", this.unitForm.id);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message || "Unit deleted") || alert("Unit deleted");
          this.openNewUnit();
          await this.fetchOrgUnits();
        } else {
          throw new Error(res?.data?.message || "Failed to delete unit");
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) || alert("Error: " + error.message);
      } finally {
        this.deletingUnit = false;
      }
    },
    async assignMember() {
      if (!this.selectedUnitId) {
        this.$toast?.error("Select a unit first") || alert("Select a unit first");
        return;
      }
      if (!this.memberForm.identifier) {
        this.$toast?.error("Employee identifier is required") || alert("Employee identifier is required");
        return;
      }
      if (!this.memberForm.setAsManager && !this.memberForm.reportsTo) {
        const selectedUnit = this.findUnitInTree(this.selectedUnitId);
        if (!selectedUnit?.manager_national_id && !selectedUnit?.manager_emp_no) {
          this.$toast?.error("Select a supervisor or configure the unit manager first") ||
            alert("Select a supervisor or configure the unit manager first");
          return;
        }
      }
      this.addingMember = true;
      try {
        const data = new FormData();
        data.append("function", "assignOrgUnitMember");
        data.append("orgUnitId", this.selectedUnitId);
        data.append("employeeIdentifier", this.memberForm.identifier);
        data.append("role", this.memberForm.role || "");
        data.append("isPrimary", this.memberForm.isPrimary);
        data.append("setAsManager", this.memberForm.setAsManager);
        data.append("reportsToIdentifier", this.memberForm.reportsTo || "");

        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message || "Member assigned") || alert("Member assigned");
          this.memberForm = {
            identifier: "",
            role: "",
            isPrimary: false,
            setAsManager: false,
            reportsTo: ""
          };
          await this.fetchOrgUnits();
        } else {
          throw new Error(res?.data?.message || "Failed to assign member");
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) || alert("Error: " + error.message);
      } finally {
        this.addingMember = false;
      }
    },
    async removeMember(member) {
      if (!member || !member.id) return;
      if (!confirm("Remove this member from the unit?")) return;
      this.removingMember = true;
      try {
        const data = new FormData();
        data.append("function", "removeOrgUnitMember");
        data.append("memberId", member.id);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message || "Member removed") || alert("Member removed");
          await this.fetchOrgUnits();
        } else {
          throw new Error(res?.data?.message || "Failed to remove member");
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) || alert("Error: " + error.message);
      } finally {
        this.removingMember = false;
      }
    },
    gotTo(route) {
      const path = route && (route.startsWith("/") || route.startsWith("http")) ? route : "/" + (route || "");
      sessionStorage.setItem("selectedSub", route || path);
      sessionStorage.setItem("currentPage", route || path);
      sessionStorage.setItem("selected", route || path.replace(/^\//, ""));
      this.$router.push(path);
    }
  }
};
</script>

<style scoped>
/* Layout: content must not overlap fixed Navigation (280px / 70px collapsed) */
#layout-wrapper {
  min-height: 100vh;
  box-sizing: border-box;
}

.content-minimize {
  margin-left: 280px;
  padding: 20px;
  min-height: 100vh;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}

#layout-wrapper.nav-collapsed .content-minimize {
  margin-left: 70px;
}

.page-content {
  max-width: 1600px;
  margin: 0 auto;
}

@media (max-width: 1024px) {
  .content-minimize {
    margin-left: 70px;
  }
}

@media (max-width: 768px) {
  .content-minimize {
    margin-left: 0;
    padding: 15px;
  }
  #layout-wrapper.nav-collapsed .content-minimize {
    margin-left: 0;
  }
}

.org-unit-list {
  max-height: 650px;
  overflow-y: auto;
}
.org-unit-list .list-group-item {
  border: none;
  border-bottom: 1px solid #f1f1f1;
}
.org-unit-list .list-group-item.active {
  background-color: #eef2ff;
  color: #2f55d4;
  font-weight: 600;
}
</style>
